<template>
  <div id="data-maintenance" class="fill-height align-start">
    <v-row class="g-container" no-gutters>
      <v-col>
        <v-row class="ma-0">
          <h2>{{ $route.meta.title }}</h2>
        </v-row>

        <v-row v-for="section in sections" :key="section.title">
          <v-col cols="12">
            <h3 class="g-heading-xs mb-n2">{{ section.title }}</h3>
          </v-col>

          <v-col
            class="d-flex"
            cols="12"
            sm="6"
            md="4"
            lg="3"
            v-for="option in section.cards.filter((o) => !o.hidden)"
            :key="option.title"
          >
            <v-card
              class="p-card flex-grow-1 px-4 px-md-6 pt-3 pt-md-4 pb-2 pb-md-4"
              :disabled="!option.routeName && !readyToTrade"
              @click="navigate(option)"
            >
              <v-row no-gutters class="p-card-content">
                <v-col class="p-card-content-info">
                  <v-row align="center">
                    <v-icon class="p-icon">{{ option.icon }}</v-icon>
                    <span class="g-heading-xs g-bold">{{ option.title }}</span>
                    <v-badge
                      v-if="option.actionableItemCount"
                      offset-x="-10"
                      color="orange"
                      :content="option.actionableItemCount"
                    ></v-badge>
                    <template v-if="option.downloadType">
                      <v-spacer></v-spacer>
                      <v-progress-circular
                        v-if="option.downloading"
                        indeterminate
                        color="secondary"
                      ></v-progress-circular>
                      <v-icon v-else class="p-icon">{{
                        option.downloadIcon
                      }}</v-icon>
                    </template>
                  </v-row>
                  <v-divider class="mt-2 mb-3" horizontal></v-divider>
                  <div class="p-text">
                    <span>{{ option.subtitle }}</span>
                    <br />
                    <ul class="mt-2 mb-4">
                      <li v-for="text in option.texts" :key="text">
                        {{ text }}
                      </li>
                    </ul>
                    <div class="mb-4" v-if="option.notes">
                      <span
                        ><strong>{{ option.notes }}</strong></span
                      >
                    </div>
                  </div>
                </v-col>

                <v-col class="p-card-content-tags">
                  <div>
                    <div v-if="option.downloadType" class="my-1">
                      <i-tag
                        :text="option.downloadText"
                        color="#dddddd"
                        textColor="black"
                        full
                      />
                    </div>

                    <div v-if="option.bulk" class="my-1">
                      <i-tag
                        :text="option.bulk.text"
                        color="#dddddd"
                        textColor="black"
                        full
                        :routeName="option.bulk.routeName"
                      />
                    </div>

                    <div
                      v-if="featureSubscriptionEnabled && option.premiumFeature"
                      class="my-1"
                    >
                      <i-tag
                        :text="'Premium only'"
                        color="#253d81"
                        textColor="white"
                        full
                      />
                    </div>
                  </div>
                </v-col>
              </v-row>
            </v-card>
          </v-col>
        </v-row>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import moment from 'moment-timezone'
import { mapActions, mapGetters, mapState } from 'vuex'
import { downloadPricebookExcel } from '@/services/pricebook'
import { downloadCategoriesHierarchyExcel } from '@/services/products'
import iTag from '@/components/Tag'

const DOWNLOAD_TYPE = {
  PRICEBOOK: 'PRICEBOOK',
  CATEGORIES: 'CATEGORIES'
}

export default {
  components: {
    iTag
  },

  data: () => ({
    downloadingPricebook: false
  }),

  computed: {
    ...mapState('preferences', ['selectedSupplierId']),
    ...mapState('profile', {
      supplierName: (state) => state.info.name
    }),
    ...mapGetters('profile', ['readyToTrade', 'creditReturnEnabled']),
    ...mapGetters('tenant', [
      'featureSubscriptionEnabled',
      'featureSupplierDirectPaymentEnabled',
      'featureSupplierDirectRebatesEnabled',
      'featureCreditReturnAutoEnrolmentEnabled',
      'featureCouponsEnabled',
      'isILR',
      'buyGetOptionEnabled',
      'tenantCountryRegion'
    ]),
    ...mapGetters('dataMaintenance', [
      'productsRequiringAmendmentsCount',
      'promotionsRequiringAmendmentsCount',
      'priceDropsRequiringAmendmentsCount',
      'volumePromotionsRequiringAmendmentsCount'
    ]),
    sections() {
      return [
        {
          title: 'Product Range / Pricing',
          cards: this.cardsRangePricing
        },
        {
          title: `Supplier Trading Rules ${this.featureSupplierDirectRebatesEnabled ? '& Rebates' : ''}`,
          cards: [...this.cardsTradingRules, ...this.cardsBuyGetFree]
        },
        {
          title: 'Promotions',
          cards: this.cardsPromotions
        }
      ]
    },

    cardsRangePricing() {
      return [
        {
          title: 'Range & Pricing Details',
          subtitle: 'Add, edit or delete',
          icon: 'mdi-hammer-screwdriver',
          routeName: 'products',
          bulk: {
            text: 'Bulk Product Uploads',
            routeName: 'products-bulk-upload'
          },
          texts: [
            'Add Product details / pricing / images',
            'Products Live, Upcoming, Approval, Draft, Archive',
            'Manage stock availability - live status',
            'Bulk Pricing Update',
            'Bulk Product Upload'
          ],
          actionableItemCount: this.productsRequiringAmendmentsCount
        },
        {
          title: 'Pricebook',
          subtitle: 'View & export your Live products only',
          icon: 'mdi-file-excel-outline',
          notes:
            'Edit pricing / data issues in Range & Pricing Details / Specials, Price Drops & Volume Buys sections',
          texts: [
            'Product details & pricing',
            'Specials, Price Drops & Volume Buys'
          ],
          downloadIcon: 'mdi-arrow-down-bold',
          downloading: false,
          downloadText: 'Export Pricebook',
          downloadType: DOWNLOAD_TYPE.PRICEBOOK
        },
        {
          title: 'Categories Hierarchy',
          subtitle: 'Complete listing by',
          icon: 'mdi-file-excel-outline',
          texts: ['Trading Department', 'Category', 'Sub Category'],
          downloadIcon: 'mdi-arrow-down-bold',
          downloading: false,
          downloadText: 'Export Categories',
          downloadType: DOWNLOAD_TYPE.CATEGORIES
        },
        {
          title: 'Zone Postcode Details',
          subtitle: 'Postcode listing by',
          icon: 'mdi-email-outline',
          routeName: 'zone-postcode',
          texts: ['Zone', 'State'],
          hidden: this.tenantCountryRegion === 'US'
        },
        {
          title: 'Tax Viewer (US TIC)',
          subtitle: 'Read-only list',
          icon: 'mdi-format-list-bulleted',
          routeName: 'us-tax-codes',
          texts: ['Search by TIC code', 'Search by label'],
          hidden: this.tenantCountryRegion !== 'US'
        }
      ]
    },

    cardsTradingRules() {
      return [
        {
          title: 'Supplier Trading Rules',
          subtitle: 'Add or edit',
          icon: 'mdi-truck-outline',
          routeName: 'trading-rules',
          texts: [
            'Minimum Order value',
            'Free Freight value',
            'Additional Freight Cost per Carton by Geography (National, State or Zone)'
          ]
        },
        {
          title: 'Supplier & Brand Logos',
          subtitle: 'Add or edit',
          icon: 'mdi-image-multiple-outline',
          routeName: 'brand-logos',
          texts: [
            'Main Supplier Logo',
            'Brand Logos (up to 4 brands)',
            `Displayed in ${this.isILR ? 'Customer' : 'Retailer'} Marketplace`
          ],
          premiumFeature: true
        },
        {
          title: 'Rebates',
          subtitle: 'Add, edit or delete',
          icon: 'mdi-sale',
          routeName: 'rebates',
          texts: [
            'Rebate % by date range',
            'Active, Upcoming or Past Rebates',
            `Target Banner(s) or MSO(s) or ${this.isILR ? 'Customer' : 'Retailer'}(s)`,
            'National or State options'
          ],
          hidden: !this.featureSupplierDirectRebatesEnabled
        },
        {
          title: 'Credit Returns',
          subtitle: 'Add, edit or delete',
          icon: 'mdi-credit-card-marker-outline',
          routeName: 'credit-return-availability',
          texts: [
            'Setup Credit Returns',
            `Target Banner(s) or MSO(s) or ${this.isILR ? 'Customer' : 'Retailer'}(s)`,
            'National or State options'
          ],
          hidden:
            !this.creditReturnEnabled ||
            this.featureCreditReturnAutoEnrolmentEnabled
        },
        {
          title: 'Payment Days',
          subtitle: 'Add, edit or delete',
          icon: 'mdi-credit-card-marker-outline',
          routeName: 'payment-days',
          texts: [
            'Payment Days by date range',
            'Active, Upcoming or Past Payment Days',
            `Target MSO(s) or ${this.isILR ? 'Customer' : 'Retailer'}(s)`,
            'National or State options'
          ],
          hidden: !this.featureSupplierDirectPaymentEnabled
        },
        {
          title: 'Family Groups',
          subtitle: 'Add, edit or delete',
          icon: 'mdi-shape-outline',
          routeName: 'family-groups',
          texts: [
            'Setup Family Group name & image',
            'Same product pricing, taxes & carton qty',
            'For Specials, Price Drops & Volume Buys',
            'Excludes variable weight products'
          ]
        }
      ]
    },

    cardsPromotions() {
      return [
        {
          title: 'Price Drops',
          subtitle: 'Add, edit or delete',
          icon: 'mdi-trending-down',
          routeName: 'price-drops',
          texts: [
            'Discount off carton price',
            'Active, Upcoming or Past Price Drops',
            'By Product or Family Group',
            `Target Banner(s) or MSO(s) or ${this.isILR ? 'Customer' : 'Retailer'}(s)`
          ],
          actionableItemCount: this.priceDropsRequiringAmendmentsCount
        },
        {
          title: 'Specials',
          subtitle: 'Add, edit or delete',
          icon: 'mdi-tag-outline',
          routeName: 'specials',
          bulk: {
            text: 'Load Multiple Specials',
            routeName: 'specials-multi-upload'
          },
          texts: [
            'Discount off carton price',
            'Active, Upcoming or Past Specials',
            'By Product or Family Group',
            `Target Banner(s) or MSO(s) or ${this.isILR ? 'Customer' : 'Retailer'}(s)`
          ],
          actionableItemCount: this.promotionsRequiringAmendmentsCount
        },
        {
          title: 'Volume Buys',
          subtitle: 'Add, edit or delete',
          icon: 'mdi-tag-multiple-outline',
          routeName: 'volume-buys',
          bulk: {
            text: 'Load Multiple Volume Buys',
            routeName: 'volume-buys-multi-upload'
          },
          texts: [
            'Discount off carton (up to 2 volume tiers)',
            'Active, Upcoming or Past Volume Buys',
            'By Product or Family Group',
            `Target Banner(s) or MSO(s) or ${this.isILR ? 'Customer' : 'Retailer'}(s)`
          ],
          premiumFeature: true,
          actionableItemCount: this.volumePromotionsRequiringAmendmentsCount
        },
        {
          title: 'Coupons',
          subtitle: 'Add, edit or delete',
          icon: 'mdi-percent-outline',
          routeName: 'coupons',
          texts: [
            'Set Coupon Spend & Save value',
            'Set Coupon & Discount',
            'Active, Upcoming or Past Coupons',
            `Target Banner(s) or MSO(s) or ${this.isILR ? 'Customer' : 'Retailer'}(s)`
          ],
          premiumFeature: true,
          hidden: !this.featureCouponsEnabled
        }
      ]
    },
    cardsBuyGetFree() {
      return [
        {
          title: 'Buy Get Free',
          subtitle: 'Add, edit or delete',
          icon: 'mdi-gift-outline',
          routeName: 'buy-get-free',
          texts: [
            'Setup Buy Get Free promotions',
            'Active, Upcoming or Past Buy Get Free offers',
            'By Product or Family Group or By Range',
            `Target National or State(s)`
          ],
          premiumFeature: true,
          hidden: !this.buyGetOptionEnabled
        }
      ]
    },

    currentDateTimeForFileName() {
      return moment().format('YYYYMMDD_HHmmss')
    },

    supplierAdjustedName() {
      return this.supplierName.replace(/\s/g, '_')
    }
  },

  methods: {
    ...mapActions('notifications', ['addNotification']),

    downloadCategoriesHierarchyExcel(option) {
      console.log(this.buyGetOptionEnabled)
      option.downloading = true
      downloadCategoriesHierarchyExcel()
        .then((response) => {
          const file = new Blob([response], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          })
          const link = document.createElement('a')
          link.href = window.URL.createObjectURL(file)
          link.download = `${this.supplierAdjustedName}_flewidtech_categories_hierarchy_${this.currentDateTimeForFileName}.xlsx`
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
        })
        .catch((error) => {
          console.log(error)
          this.addNotification({
            color: 'error',
            text: 'File could not be downloaded. Please try again...'
          })
        })
        .finally(() => {
          option.downloading = false
        })
    },

    downloadPricebookExcel(option) {
      option.downloading = true
      downloadPricebookExcel(this.selectedSupplierId)
        .then((response) => {
          const file = new Blob([response], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          })
          const link = document.createElement('a')
          link.href = window.URL.createObjectURL(file)
          link.download = `${this.supplierAdjustedName}_flewidtech_pricebook_${this.currentDateTimeForFileName}.xlsx`
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
        })
        .catch((error) => {
          console.log(error)
          this.addNotification({
            color: 'error',
            text: 'File could not be downloaded. Please try again...'
          })
        })
        .finally(() => {
          option.downloading = false
        })
    },

    navigate(option) {
      if (option.routeName) {
        this.$router.push({ name: option.routeName })
        return
      }

      if (
        option.downloadType === DOWNLOAD_TYPE.PRICEBOOK &&
        this.readyToTrade
      ) {
        this.downloadPricebookExcel(option)
        return
      }

      if (
        option.downloadType === DOWNLOAD_TYPE.CATEGORIES &&
        this.readyToTrade
      ) {
        this.downloadCategoriesHierarchyExcel(option)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
#data-maintenance {
  .p-card {
    min-height: 150px;

    &-content {
      display: flex;
      height: 100%;
      flex-direction: column;
      justify-content: space-between;

      &-info {
        .p-icon {
          width: 32px;
          height: 32px;
          margin-left: 8px;
          margin-right: 8px;
          color: $secondary-color;
        }
      }

      &-tags {
        display: flex;
        align-items: flex-end;

        div {
          width: 100%;
        }
      }
    }
  }
}

@media only screen and (min-width: 960px) {
  #data-maintenance {
    .p-card {
      min-height: 175px;

      .p-text {
        * {
          font-size: 14px;
        }
      }
    }

    .mdi:before {
      font-size: 28px;
    }
  }
}
</style>
